import addDays from 'date-fns/addDays'
import addMonths from 'date-fns/addMonths'
import addWeeks from 'date-fns/addWeeks'
import addYears from 'date-fns/addYears'
import endOfDay from 'date-fns/endOfDay'
import endOfMonth from 'date-fns/endOfMonth'
import endOfWeek from 'date-fns/endOfWeek'
import endOfYear from 'date-fns/endOfYear'
import startOfDay from 'date-fns/startOfDay'
import startOfMonth from 'date-fns/startOfMonth'
import startOfWeek from 'date-fns/startOfWeek'
import startOfYear from 'date-fns/startOfYear'
import subDays from 'date-fns/subDays'
import subMonths from 'date-fns/subMonths'
import subWeeks from 'date-fns/subWeeks'
import subYears from 'date-fns/subYears'
import { z } from 'zod'
import { enumerated } from '../enumerated'
import { dateOrIsoString } from '../date'

export const timeTypeForCurrentOnly = enumerated({
  current: 'Atual',
})
export function timeForCurrentOnly() {
  return z.object({
    type: timeTypeForCurrentOnly.describe('Tempo').default('current'),
    title: z.string().optional(),
  })
}

export const timeTypeForPrevalence = enumerated({
  customDate: 'Data personalizada',
  current: 'Atual',
})
export function timeForPrevalence() {
  return z.object({
    type: timeTypeForPrevalence.describe('Tempo').default('current'),
    date: dateOrIsoString().describe('Data').nullish(),
  })
}

export const timeTypeForIncidenceRealtime = enumerated({
  currentYear: 'Ano atual',
  currentYearSubOneDay: 'Ano atual até ontem',
  previousYear: 'Ano anterior',
  currentMonth: 'Mês atual',
  previousMonth: 'Mês anterior',
  currentWeek: 'Semana atual',
  previousWeek: 'Semana anterior',
  currentDay: 'Dia atual',
  previousDay: 'Dia anterior',
  last90Days: 'Últimos 90 dias',
  last60Days: 'Últimos 60 dias',
  last30Days: 'Últimos 30 dias',
  last7Days: 'Últimos 7 dias',
  last24Hours: 'Últimas 24 horas',
  customDateRange: 'Intervalo de datas personalizado',
})

export const timeTypeForIncidenceRealtimePrediction = enumerated({
  currentYearPredicted: 'Ano atual (predição)',
  nextYearPredicted: 'Ano seguinte (predição)',
  currentMonthPredicted: 'Mês atual (predição)',
  nextMonthPredicted: 'Mês seguinte (predição)',
  currentWeekPredicted: 'Semana atual (predição)',
  nextWeekPredicted: 'Semana seguinte (predição)',
  currentDayPredicted: 'Dia atual (predição)',
  nextDayPredicted: 'Dia seguinte (predição)',
  next90DaysPredicted: 'Próximos 90 dias (predição)',
  next60DaysPredicted: 'Próximos 60 dias (predição)',
  next30DaysPredicted: 'Próximos 30 dias (predição)',
  next7DaysPredicted: 'Próximos 7 dias (predição)',
  next24HoursPredicted: 'Próximas 24 horas (predição)',
  customDateRangePredicted: 'Intervalo de datas personalizado (predição)',
})

export const timeTypeForIncidenceCompatibility = enumerated({
  last90DaysOfData: 'Últimos 90 dias de dados',
  last60DaysOfData: 'Últimos 60 dias de dados',
  last30DaysOfData: 'Últimos 30 dias de dados',
  last7DaysOfData: 'Últimos 7 dias de dados',
  last24HoursOfData: 'Últimas 24 horas de dados',
  customDateRange: 'Intervalo de datas personalizado',
})

type DateRange = [Date, Date]

export const generateIncidenceDates = {
  // Incidence
  currentYear: (end: Date = new Date()): DateRange => [startOfYear(end), end],
  currentYearSubOneDay: (end: Date = new Date()): DateRange => [startOfYear(end), subDays(end, 1)],
  previousYear: (end: Date = new Date()): DateRange => {
    const start = startOfYear(subYears(end, 1))
    const finish = endOfYear(subYears(end, 1))
    return [start, finish]
  },
  currentMonth: (end: Date = new Date()): DateRange => [startOfMonth(end), end],
  previousMonth: (end: Date = new Date()): DateRange => {
    const start = startOfMonth(subMonths(end, 1))
    const finish = endOfMonth(subMonths(end, 1))
    return [start, finish]
  },
  currentWeek: (end: Date = new Date()): DateRange => [startOfWeek(end, { weekStartsOn: 1 }), end],
  previousWeek: (end: Date = new Date()): DateRange => {
    const start = startOfWeek(subWeeks(end, 1), { weekStartsOn: 1 })
    const finish = endOfWeek(subWeeks(end, 1), { weekStartsOn: 1 })
    return [start, finish]
  },
  currentDay: (end: Date = new Date()): DateRange => [startOfDay(end), end],
  previousDay: (end: Date = new Date()): DateRange => {
    const start = startOfDay(subDays(end, 1))
    const finish = endOfDay(subDays(end, 1))
    return [start, finish]
  },
  last10Years: (end: Date = new Date()): DateRange => [subYears(end, 10), end],
  last90Days: (end: Date = new Date()): DateRange => [subDays(end, 90), end],
  last60Days: (end: Date = new Date()): DateRange => [subDays(end, 60), end],
  last30Days: (end: Date = new Date()): DateRange => [subDays(end, 30), end],
  last7Days: (end: Date = new Date()): DateRange => [subDays(end, 7), end],
  last24Hours: (end: Date = new Date()): DateRange => [subDays(end, 1), end],
  customDateRange: (end: Date = new Date()): DateRange => {
    const start = startOfDay(subDays(end, 1))
    const finish = endOfDay(subDays(end, 1))
    return [start, finish]
  },

  // Incidence prediction
  currentYearPredicted: (start: Date = new Date()): DateRange => [
    startOfYear(start),
    endOfYear(start),
  ],
  nextYearPredicted: (start: Date = new Date()): DateRange => {
    const nextYearStart = addYears(startOfYear(start), 1)
    const nextYearEnd = endOfYear(nextYearStart)
    return [nextYearStart, nextYearEnd]
  },
  currentMonthPredicted: (start: Date = new Date()): DateRange => [
    startOfMonth(start),
    endOfMonth(start),
  ],
  nextMonthPredicted: (start: Date = new Date()): DateRange => {
    const nextMonthStart = addMonths(startOfMonth(start), 1)
    const nextMonthEnd = endOfMonth(nextMonthStart)
    return [nextMonthStart, nextMonthEnd]
  },
  currentWeekPredicted: (start: Date = new Date()): DateRange => [
    startOfWeek(start, { weekStartsOn: 1 }),
    endOfWeek(start, { weekStartsOn: 1 }),
  ],
  nextWeekPredicted: (start: Date = new Date()): DateRange => {
    const nextWeekStart = addWeeks(startOfWeek(start, { weekStartsOn: 1 }), 1)
    const nextWeekEnd = endOfWeek(nextWeekStart, { weekStartsOn: 1 })
    return [nextWeekStart, nextWeekEnd]
  },
  currentDayPredicted: (start: Date = new Date()): DateRange => [
    startOfDay(start),
    endOfDay(start),
  ],
  nextDayPredicted: (start: Date = new Date()): DateRange => {
    const nextDayStart = addDays(startOfDay(start), 1)
    const nextDayEnd = endOfDay(nextDayStart)
    return [nextDayStart, nextDayEnd]
  },
  next90DaysPredicted: (start: Date = new Date()): DateRange => [start, addDays(start, 90)],
  next60DaysPredicted: (start: Date = new Date()): DateRange => [start, addDays(start, 60)],
  next30DaysPredicted: (start: Date = new Date()): DateRange => [start, addDays(start, 30)],
  next7DaysPredicted: (start: Date = new Date()): DateRange => [start, addDays(start, 7)],
  next24HoursPredicted: (start: Date = new Date()): DateRange => [start, addDays(start, 1)],
  customDateRangePredicted: (start: Date = new Date()): DateRange => {
    const nextDayStart = addDays(startOfDay(start), 1)
    const nextDayEnd = endOfDay(nextDayStart)
    return [nextDayStart, nextDayEnd]
  },

  // Incidence compatibility
  last90DaysOfData: (lastDateWithData: Date = new Date()): DateRange => [
    subDays(lastDateWithData, 90),
    lastDateWithData,
  ],
  last60DaysOfData: (lastDateWithData: Date = new Date()): DateRange => [
    subDays(lastDateWithData, 60),
    lastDateWithData,
  ],
  last30DaysOfData: (lastDateWithData: Date = new Date()): DateRange => [
    subDays(lastDateWithData, 30),
    lastDateWithData,
  ],
  last7DaysOfData: (lastDateWithData: Date = new Date()): DateRange => [
    subDays(lastDateWithData, 7),
    lastDateWithData,
  ],
  last24HoursOfData: (lastDateWithData: Date = new Date()): DateRange => [
    subDays(lastDateWithData, 1),
    lastDateWithData,
  ],
}

export const incidenceTimeTypes: string[] = [
  ...Object.keys(timeTypeForIncidenceRealtime.getMeta()?.keysAndLabels as { [x: string]: string }),
  ...Object.keys(
    timeTypeForIncidenceRealtimePrediction.getMeta()?.keysAndLabels as { [x: string]: string },
  ),
  ...Object.keys(
    timeTypeForIncidenceCompatibility.getMeta()?.keysAndLabels as { [x: string]: string },
  ),
]
export const currentOnlyTimeTypes = ['current']
export const prevalenceTimeTypes = ['current', 'customDate']

export const timeTypeForIncidence = enumerated({
  ...(timeTypeForIncidenceRealtime.getMeta()?.keysAndLabels || {}),
  ...(timeTypeForIncidenceRealtimePrediction.getMeta()?.keysAndLabels || {}),
  ...(timeTypeForIncidenceCompatibility.getMeta()?.keysAndLabels || {}),
})

const timeGroupType = enumerated({
  daily: 'Diariamente',
  weekly: 'Semanalmente',
  monthly: 'Mensalmente',
  yearly: 'Anualmente',
})

export function timeForIncidence() {
  return z.object({
    type: timeTypeForIncidence.describe('Tempo').nullish(),
    startDate: dateOrIsoString().describe('Data inicial').nullish(),
    endDate: dateOrIsoString().describe('Data final').nullish(),
    groupType: timeGroupType.describe('Tipo de agregação').nullish(),
  })
}

export type TimeForCurrentOnly = z.infer<ReturnType<typeof timeForCurrentOnly>>
export type TimeForPrevalence = z.infer<ReturnType<typeof timeForPrevalence>>
export type TimeForIncidence = z.infer<ReturnType<typeof timeForIncidence>>
export type TimeInput = TimeForCurrentOnly | TimeForPrevalence | TimeForIncidence
